/**
 * IPFS Utilities
 * 
 * Helpers for working with IPFS URLs, fetching metadata, and uploading files.
 */

// IPFS Gateway for converting ipfs:// URLs to HTTP
const IPFS_GATEWAY = 'https://ipfs.flaunch.gg/ipfs/';
const FALLBACK_GATEWAY = 'https://ipfs.io/ipfs/';

// Pinata API endpoints
const PINATA_API_URL = 'https://api.pinata.cloud';

/**
 * Upload a file buffer to IPFS via Pinata
 * Server-side only - requires PINATA_JWT env var
 */
export async function uploadToIPFS(
  buffer: Buffer,
  filename: string,
  contentType: string = 'image/png'
): Promise<{ ipfsHash: string; url: string } | null> {
  const jwt = process.env.PINATA_JWT;
  if (!jwt) {
    console.error('[IPFS] PINATA_JWT not configured');
    return null;
  }

  try {
    // Create form data with the file
    const formData = new FormData();
    // Use type assertion to work around TypeScript strictness with Buffer/Blob types
    const blob = new Blob([buffer as unknown as BlobPart], { type: contentType });
    formData.append('file', blob, filename);

    // Add metadata
    const metadata = JSON.stringify({
      name: filename,
      keyvalues: {
        type: 'avatar',
        uploadedAt: new Date().toISOString(),
      },
    });
    formData.append('pinataMetadata', metadata);

    // Upload to Pinata
    const response = await fetch(`${PINATA_API_URL}/pinning/pinFileToIPFS`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.text();
      console.error('[IPFS] Upload failed:', error);
      return null;
    }

    const result = await response.json();
    const ipfsHash = result.IpfsHash;

    return {
      ipfsHash,
      url: `${IPFS_GATEWAY}${ipfsHash}`,
    };
  } catch (error) {
    console.error('[IPFS] Upload error:', error);
    return null;
  }
}

/**
 * Upload a JSON object to IPFS via Pinata
 * Server-side only - requires PINATA_JWT env var
 */
export async function uploadJsonToIPFS(
  json: Record<string, unknown>,
  name: string = 'metadata.json'
): Promise<{ ipfsHash: string; url: string } | null> {
  const jwt = process.env.PINATA_JWT;
  if (!jwt) {
    console.error('[IPFS] PINATA_JWT not configured');
    return null;
  }

  try {
    const response = await fetch(`${PINATA_API_URL}/pinning/pinJSONToIPFS`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        pinataContent: json,
        pinataMetadata: {
          name,
          keyvalues: {
            type: 'token-metadata',
            uploadedAt: new Date().toISOString(),
          },
        },
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      console.error('[IPFS] JSON upload failed:', error);
      return null;
    }

    const result = await response.json();
    const ipfsHash = result.IpfsHash;

    return {
      ipfsHash,
      url: `${IPFS_GATEWAY}${ipfsHash}`,
    };
  } catch (error) {
    console.error('[IPFS] JSON upload error:', error);
    return null;
  }
}

/**
 * Download an image from a URL and upload it to IPFS
 * Useful for permanently storing social media avatars
 */
export async function mirrorToIPFS(
  imageUrl: string,
  filename?: string
): Promise<{ ipfsHash: string; url: string } | null> {
  try {
    // Download the image
    const response = await fetch(imageUrl, {
      signal: AbortSignal.timeout(10000), // 10 second timeout
    });

    if (!response.ok) {
      console.error('[IPFS] Failed to download image:', response.status);
      return null;
    }

    // Get content type
    const contentType = response.headers.get('content-type') || 'image/png';
    
    // Get the image data
    const arrayBuffer = await response.arrayBuffer();
    const buffer = Buffer.from(arrayBuffer);

    // Generate filename if not provided
    const ext = contentType.includes('jpeg') || contentType.includes('jpg') ? 'jpg' 
              : contentType.includes('gif') ? 'gif'
              : contentType.includes('webp') ? 'webp'
              : 'png';
    const name = filename || `avatar-${Date.now()}.${ext}`;

    // Upload to IPFS
    return await uploadToIPFS(buffer, name, contentType);
  } catch (error) {
    console.error('[IPFS] Mirror error:', error);
    return null;
  }
}

/**
 * Convert an IPFS URL to an HTTP gateway URL
 * Handles: ipfs://QmXXX, ipfs://QmXXX/file.json, /ipfs/QmXXX
 */
export function ipfsToHttp(ipfsUrl: string | undefined | null): string | undefined {
  if (!ipfsUrl) return undefined;
  
  // Rewrite legacy Pinata public gateway URLs to the preferred gateway
  if (ipfsUrl.includes('gateway.pinata.cloud/ipfs/')) {
    const cid = ipfsUrl.split('/ipfs/').pop();
    return cid ? `${IPFS_GATEWAY}${cid}` : ipfsUrl;
  }

  // Already an HTTP URL
  if (ipfsUrl.startsWith('http://') || ipfsUrl.startsWith('https://')) {
    return ipfsUrl;
  }
  
  // Handle ipfs:// protocol
  if (ipfsUrl.startsWith('ipfs://')) {
    const cid = ipfsUrl.replace('ipfs://', '');
    return `${IPFS_GATEWAY}${cid}`;
  }
  
  // Handle /ipfs/ path
  if (ipfsUrl.startsWith('/ipfs/')) {
    const cid = ipfsUrl.replace('/ipfs/', '');
    return `${IPFS_GATEWAY}${cid}`;
  }
  
  // Assume it's a raw CID
  if (ipfsUrl.startsWith('Qm') || ipfsUrl.startsWith('bafy')) {
    return `${IPFS_GATEWAY}${ipfsUrl}`;
  }
  
  return ipfsUrl;
}

/**
 * ERC721 Metadata JSON structure
 */
export interface TokenMetadata {
  name?: string;
  symbol?: string;
  description?: string;
  image?: string;
  erc20Image?: string;
  external_url?: string;
  attributes?: Array<{ trait_type: string; value: string | number }>;
}

/**
 * Fetch and parse ERC721 metadata from a token URI
 */
export async function fetchTokenMetadata(tokenUri: string): Promise<TokenMetadata | null> {
  try {
    const httpUrl = ipfsToHttp(tokenUri);
    if (!httpUrl) return null;
    
    const response = await fetch(httpUrl, {
      next: { revalidate: 3600 }, // Cache for 1 hour
    });
    
    if (!response.ok) {
      console.warn(`[IPFS] Failed to fetch metadata from ${httpUrl}: ${response.status}`);
      return null;
    }
    
    const metadata = await response.json();
    return metadata as TokenMetadata;
  } catch (error) {
    console.error('[IPFS] Error fetching token metadata:', error);
    return null;
  }
}

/**
 * Extract the image URL from a token URI
 * Fetches the metadata JSON and returns the image field
 */
export async function getImageFromTokenUri(tokenUri: string): Promise<string | undefined> {
  const metadata = await fetchTokenMetadata(tokenUri);
  if (!metadata?.image) return undefined;
  
  // Convert the image URL to HTTP if it's an IPFS URL
  return ipfsToHttp(metadata.image);
}
